import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Table, Input, Select, Row, Col, Card, DatePicker } from "antd";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import PropertyVisitdeleteModal from "../../../components/modelpopup/PropertyVisitdeleteModal";
import moment from "moment";
import { AddPreDisbursalCollectionVisitModel } from "./AddPreDisbursalCollectionVisit";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const PreDisbursalCollectionVisitList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(null);

  const [searchFilters, setSearchFilters] = useState({
    applicationNo: "",
    name: "",
    branch: "",
    verificationStatus: "",
    dateRange: null,
  });

  const parseDate = useCallback((dateString) => {
    if (!dateString) return null;

    const dateStr = String(dateString).trim();
    if (dateStr.includes("T")) {
      const cleanedDate = dateStr.split(".")[0]; // Remove microseconds
      const parsed = moment(cleanedDate);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Fallback to other formats if needed
    const formats = [
      "YYYY-MM-DD HH:mm:ss",
      "DD/MM/YYYY H:mm",
      "DD-MM-YYYY H:mm",
      "D/M/YYYY H:mm",
      "D-M-YYYY H:mm",
    ];

    for (const format of formats) {
      const parsed = moment(dateStr, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Try default moment parsing as last resort
    const defaultParsed = moment(dateStr);
    return defaultParsed.isValid() ? defaultParsed : null;
  }, []);

  // Get unique branches from data
  const branches = useMemo(() => {
    return [...new Set(data.map((item) => item.branchName).filter(Boolean))];
  }, [data]);

  // FIXED: Simplified and more reliable date filtering
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Filter by Application Number
    if (searchFilters.applicationNo.trim()) {
      filtered = filtered.filter((item) =>
        item.applicationNo
          ?.toLowerCase()
          .includes(searchFilters.applicationNo.toLowerCase().trim())
      );
    }

    // Filter by Name
    if (searchFilters.name.trim()) {
      filtered = filtered.filter((item) =>
        item.name
          ?.toLowerCase()
          .includes(searchFilters.name.toLowerCase().trim())
      );
    }

    // Filter by Branch
    if (searchFilters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === searchFilters.branch.toLowerCase()
      );
    }

    // Filter by Verification Status
    if (searchFilters.verificationStatus) {
      filtered = filtered.filter(
        (item) =>
          item.verificationStatus?.toLowerCase() ===
          searchFilters.verificationStatus.toLowerCase()
      );
    }

    // FIXED: Simplified date range filtering
    if (searchFilters.dateRange && searchFilters.dateRange.length === 2) {
      const [startDate, endDate] = searchFilters.dateRange;

      console.log("=== DATE FILTERING ===");
      console.log("Filter range:", {
        start: startDate.format("YYYY-MM-DD"),
        end: endDate.format("YYYY-MM-DD"),
      });

      const beforeCount = filtered.length;

      filtered = filtered.filter((item) => {
        // Get the primary date field (created seems to be the main one)
        const dateValue =
          item.created || item.createdAt || item.date || item.visitDate;

        if (!dateValue) return false;

        const itemDate = parseDate(dateValue);
        if (!itemDate) return false;

        // Simple date comparison - just compare the date part (ignore time)
        const itemDateOnly = itemDate.format("YYYY-MM-DD");
        const startDateOnly = startDate.format("YYYY-MM-DD");
        const endDateOnly = endDate.format("YYYY-MM-DD");

        const isInRange =
          itemDateOnly >= startDateOnly && itemDateOnly <= endDateOnly;

        if (isInRange) {
          console.log(
            `✅ Match: ${item.applicationNo} - Date: ${itemDateOnly}`
          );
        }

        return isInRange;
      });

      console.log("Filter results:", {
        beforeCount,
        afterCount: filtered.length,
        range: `${startDate.format("DD/MM/YYYY")} to ${endDate.format(
          "DD/MM/YYYY"
        )}`,
      });
      console.log("=== END DATE FILTERING ===");
    }

    return filtered;
  }, [data, searchFilters, parseDate]);

  // Optimized filter change handler
  const handleFilterChange = useCallback((key, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Date range change handler
  const handleDateRangeChange = useCallback((dates) => {
    console.log("Date range changed:", dates);
    setSearchFilters((prev) => ({
      ...prev,
      dateRange: dates,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setSearchFilters({
      applicationNo: "",
      name: "",
      branch: "",
      verificationStatus: "",
      dateRange: null,
    });
  }, []);

  const handleFetchAllDisbursalCase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Api_Url}api/v1/PreDisbursalVisit/superadmin/all-cases`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      // console.log("SuperAdmin all cases", response.data?.data)
      const fetchedData = response?.data?.data || [];
      setData(fetchedData);

      // Show date range in dataset
      const allDates = fetchedData
        .map((item) => {
          const dateValue =
            item.created || item.createdAt || item.date || item.visitDate;
          const parsed = parseDate(dateValue);
          return parsed ? parsed.format("YYYY-MM-DD") : null;
        })
        .filter(Boolean);

      const uniqueDates = [...new Set(allDates)].sort();
      console.log("Dataset date range:", {
        total: fetchedData.length,
        dateRange:
          uniqueDates.length > 0
            ? `${uniqueDates[0]} to ${uniqueDates[uniqueDates.length - 1]}`
            : "No dates",
        uniqueDatesCount: uniqueDates.length,
      });

      SuccessToast("All cases loaded successfully");
    } catch (error) {
      console.error("Error fetching all cases:", error);
      ErrorToast("Failed to load all cases");
    } finally {
      setLoading(false);
    }
  };

  const PropertyData = async () => {
    try {
      setLoading(true);
      const userId = JSON.parse(sessionStorage.getItem("user"))?.id;
      const response = await axios.get(
        `${Api_Url}api/v1/PreDisbursalVisit?UserId=${userId}&PageNumber=1&PageSize=100`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      console.log("PropertyData response:", response.data?.data);
      const fetchedData = response?.data?.data || [];
      setData(fetchedData);

      // Show date range in dataset
      const allDates = fetchedData
        .map((item) => {
          const dateValue =
            item.created || item.createdAt || item.date || item.visitDate;
          const parsed = parseDate(dateValue);
          return parsed ? parsed.format("YYYY-MM-DD") : null;
        })
        .filter(Boolean);

      const uniqueDates = [...new Set(allDates)].sort();
      console.log("Dataset date range:", {
        total: fetchedData.length,
        dateRange:
          uniqueDates.length > 0
            ? `${uniqueDates[0]} to ${uniqueDates[uniqueDates.length - 1]}`
            : "No dates",
        uniqueDatesCount: uniqueDates.length,
      });
    } catch (error) {
      console.error("Error fetching property data:", error);
      ErrorToast("Failed to load property data");
      return error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    PropertyData();
  }, []);

  const [deleteId, setDeleteId] = useState(null);
  const PropertyVisitDeleteFunc = async () => {
    try {
      const response = await axios.delete(
        `${Api_Url}api/v1/PropertyVisit/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      PropertyData();
      SuccessToast("Record deleted successfully");
    } catch (error) {
      console.error("Error deleting record:", error);
      ErrorToast("Failed to delete record");
      return error.message;
    }
  };

  const handleDownload = async (record) => {
    try {
      setDownloadLoading(record.id);
      const response = await axios.get(
        `${Api_Url}api/v1/PreDisbursalVisit/generatepredisbursalpdf/${record.id}`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );

      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `PreDisbursal-${record.applicationNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(fileURL);
      document.body.removeChild(link);
      SuccessToast("Download Successful");
    } catch (error) {
      console.error("Download error:", error);
      ErrorToast("Something went wrong");
    } finally {
      setDownloadLoading(null);
    }
  };

  const handleExportAllCasesToExcel = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/PreDisbursalVisit/superadmin/export-all-cases`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );

      console.log("Response type:", response.data.type);
      console.log("Response size:", response.data.size);

      let file,
        fileName,
        currentDate = new Date().toISOString().split("T")[0];

      if (response.data.type === "text/csv") {
        file = new Blob([response.data], { type: "text/csv" });
        fileName = `PreDisbursalVisit_AllCases_${currentDate}.csv`;
      } else {
        file = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        fileName = `PreDisbursalVisit_AllCases_${currentDate}.xlsx`;
      }

      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(fileURL);
      document.body.removeChild(link);

      SuccessToast(
        `Export successful - ${
          response.data.type === "text/csv" ? "CSV" : "Excel"
        } file downloaded`
      );
    } catch (error) {
      console.error("Error exporting:", error);
      ErrorToast("Failed to export file");
    }
  };

  // Helper function to get the display date from record
  const getRecordDate = useCallback(
    (record) => {
      const dateValue =
        record.created || record.createdAt || record.date || record.visitDate;
      const parsed = parseDate(dateValue);
      return parsed ? parsed.format("MMMM Do YYYY, h:mm:ss a") : "Invalid Date";
    },
    [parseDate]
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text, record) => (
        <span className="table-avatar">
          <Link to="/profile" className="avatar">
            <img alt="" src={record.visitSelfieUrl || "/placeholder.svg"} />
          </Link>
          <Link to="/profile">{text}</Link>
        </span>
      ),
    },
    {
      title: "Branch Name",
      dataIndex: "branchName",
    },
    {
      title: "Application Number",
      dataIndex: "applicationNo",
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
    },
    {
      title: "Verification Status",
      dataIndex: "verificationStatus",
      render: (status) => (
        <span
          className={`badge ${
            status?.toLowerCase() === "negative"
              ? "badge-danger"
              : status?.toLowerCase() === "positive"
              ? "badge-success"
              : "badge-warning"
          }`}
        >
          {status?.toUpperCase()}
        </span>
      ),
    },
    {
      title: "Visit Remarks",
      dataIndex: "visitRemark",
      render: (visitRemark, record) => (
        <TextArea
          style={{ width: "200px", color: "black" }}
          rows={3}
          maxLength={50}
          disabled
          value={record.visitRemark}
        />
      ),
    },
    {
      title: "Last Activity",
      dataIndex: "created",
      render: (created, record) => (
        <span className="table-avatar">{getRecordDate(record)}</span>
      ),
    },
    {
      title: "Download",
      dataIndex: "id",
      render: (text, record) => (
        <Button
          onClick={() => handleDownload(record)}
          loading={downloadLoading === record.id}
          disabled={downloadLoading === record.id}
        >
          {downloadLoading === record.id ? (
            <>
              <i className="fa-solid fa-spinner fa-spin "></i>
            </>
          ) : (
            <i className="fa-solid fa-download"></i>
          )}
        </Button>
      ),
    },
    {
      title: "Location",
      dataIndex: "",
      render: (text, record) => (
        <a
          href={`https://www.google.com/maps?q=${record.latitude},${record.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="fa-solid fa-location-dot"></i>
        </a>
      ),
    },
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="row">
            <div className="col-12 d-flex align-items-center justify-content-between">
              <h3 className="mb-0">Pre-Disbursal Collection Visit</h3>
              <div className="d-flex align-items-center gap-3">
                {sessionStorage.getItem("user") &&
                ["AdminFacilities", "SuperAdmin"].some((role) =>
                  JSON.parse(sessionStorage.getItem("user")).roles?.includes(
                    role
                  )
                ) ? (
                  <>
                    <button
                      onClick={handleFetchAllDisbursalCase}
                      className="btn border mb-2 items-center submit-btn"
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Show All Cases"}
                    </button>
                    <button
                      onClick={handleExportAllCasesToExcel}
                      className="btn btn-success mb-2 items-center submit-btn"
                    >
                      <i className="fa-solid fa-file-excel me-2"></i>
                      Export Excel
                    </button>
                  </>
                ) : (
                  ""
                )}

                <button
                  className="btn btn-primary submit-btn mb-2"
                  onClick={handleOpenModal}
                >
                  Add Pre-Disbursal Visit
                </button>
                {isModalOpen && (
                  <AddPreDisbursalCollectionVisitModel
                    onClose={handleCloseModal}
                    loadPropertDataFunction={PropertyData}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <Card className="mb-4">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Search
                  placeholder="Search by Application No"
                  value={searchFilters.applicationNo}
                  onChange={(e) =>
                    handleFilterChange("applicationNo", e.target.value)
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Search
                  placeholder="Search by Name"
                  value={searchFilters.name}
                  onChange={(e) => handleFilterChange("name", e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Select
                  placeholder="Select Branch"
                  value={searchFilters.branch || undefined}
                  onChange={(value) => handleFilterChange("branch", value)}
                  allowClear
                  style={{ width: "100%" }}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {branches.map((branch) => (
                    <Option key={branch} value={branch}>
                      {branch}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6} lg={4}>
                <Select
                  placeholder="Verification Status"
                  value={searchFilters.verificationStatus || undefined}
                  onChange={(value) =>
                    handleFilterChange("verificationStatus", value)
                  }
                  allowClear
                  style={{ width: "100%" }}
                >
                  <Option value="positive">Positive</Option>
                  <Option value="negative">Negative</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6} lg={8}>
                <RangePicker
                  placeholder={["Start Date", "End Date"]}
                  value={searchFilters.dateRange}
                  onChange={handleDateRangeChange}
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  allowClear
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <Button onClick={clearFilters} type="default">
                  Clear All Filters
                </Button>
                <span className="ms-3 text-muted">
                  Showing {filteredData.length} of {data.length} records
                  {searchFilters.dateRange &&
                    searchFilters.dateRange.length === 2 && (
                      <span>
                        {" "}
                        (filtered by date:{" "}
                        {searchFilters.dateRange[0].format(
                          "DD/MM/YYYY"
                        )} to {searchFilters.dateRange[1].format("DD/MM/YYYY")})
                      </span>
                    )}
                </span>
              </Col>
            </Row>
          </Card>

          {/* Table Section */}
          <div className="row mt-4">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  className="table-striped"
                  style={{ overflowX: "auto" }}
                  columns={columns}
                  dataSource={filteredData}
                  rowKey={(record) => record.id}
                  loading={loading}
                  pagination={{
                    showSizeChanger: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    pageSizeOptions: ["10", "25", "50", "100"],
                    defaultPageSize: 10,
                    showLessItems: true,
                    responsive: true,
                    position: ["bottomCenter"],
                  }}
                  scroll={{ x: 800 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <PropertyVisitdeleteModal
        Name="Delete Client"
        propertyDeleteFunc={PropertyVisitDeleteFunc}
      />
    </>
  );
};

export default PreDisbursalCollectionVisitList;
